import { Zap, Tag, FileText, Settings, Coins, ArrowRight, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "@/hooks/useReducedMotion";

const features = [
  {
    icon: Zap,
    title: "AI Extraction",
    pain: "CRMs make you set up fields, workflows, and integrations.",
    solution: "Parses signature blocks and email context to capture name, company, title, phone, email, website and address.",
    emoji: "⚡",
    bgColor: "bg-brand-orange/10",
    hoverBg: "group-hover:bg-brand-orange/20",
    textColor: "text-brand-orange",
    gradient: "from-brand-orange/20 to-brand-orange/5",
  },
  {
    icon: Tag,
    title: "Smart Categories",
    pain: "CRM tagging systems force you into their generic schemas.",
    solution: "Auto-assigns your custom categories such as Customer, Partner, Supplier, Investor or Lead.",
    emoji: "🏷️",
    bgColor: "bg-brand-cyan/10",
    hoverBg: "group-hover:bg-brand-cyan/20",
    textColor: "text-brand-cyan",
    gradient: "from-brand-cyan/20 to-brand-cyan/5",
  },
  {
    icon: FileText,
    title: "Smart Notes",
    pain: "Researching people or companies means opening 10 browser tabs.",
    solution: "Generates concise, helpful notes using firmographic context to remember who the contact is and why they matter.",
    emoji: "📝",
    bgColor: "bg-brand-pink/10",
    hoverBg: "group-hover:bg-brand-pink/20",
    textColor: "text-brand-pink",
    gradient: "from-brand-pink/20 to-brand-pink/5",
  },
  {
    icon: Settings,
    title: "Field Presets",
    pain: "CRMs store every field imaginable. Most are useless.",
    solution: "Apply predefined values (e.g., default country code, department, source) to keep your address book consistent.",
    emoji: "⚙️",
    bgColor: "bg-brand-blue/10",
    hoverBg: "group-hover:bg-brand-blue/20",
    textColor: "text-brand-blue",
    gradient: "from-brand-blue/20 to-brand-blue/5",
  },
  {
    icon: Coins,
    title: "Adaptive Credits",
    pain: "CRM subscriptions charge you whether you use them or not.",
    solution: "Credit-based model without mandatory subscriptions. Pay only for what you use.",
    emoji: "💰",
    bgColor: "bg-brand-orange/10",
    hoverBg: "group-hover:bg-brand-orange/20",
    textColor: "text-brand-orange",
    gradient: "from-brand-orange/20 to-brand-orange/5",
  },
];

export const Features = () => {
  const [visibleCards, setVisibleCards] = useState<boolean[]>(new Array(features.length).fill(false));
  const [headerVisible, setHeaderVisible] = useState(false);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const headerRef = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      setHeaderVisible(true);
      setVisibleCards(new Array(features.length).fill(true));
      return;
    }

    // Header observer
    const headerObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setHeaderVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (headerRef.current) {
      headerObserver.observe(headerRef.current);
    }

    // Card observers
    const observers = cardRefs.current.map((card, index) => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setTimeout(() => {
                setVisibleCards((prev) => {
                  const newVisible = [...prev];
                  newVisible[index] = true;
                  return newVisible;
                });
              }, index * 100);
            }
          });
        },
        { threshold: 0.1 }
      );
      
      if (card) observer.observe(card);
      return observer;
    });

    return () => {
      headerObserver.disconnect();
      observers.forEach((observer, index) => {
        if (cardRefs.current[index]) {
          observer.unobserve(cardRefs.current[index]!);
        }
      });
    };
  }, [prefersReducedMotion]);

  return (
    <section id="features" className="py-20 md:py-32 bg-background relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-64 md:w-96 h-64 md:h-96 bg-brand-pink/10 rounded-full blur-[120px] animate-blob" />
        <div className="absolute bottom-0 right-1/4 w-64 md:w-96 h-64 md:h-96 bg-brand-cyan/10 rounded-full blur-[120px] animate-blob animation-delay-4000" />
      </div>

      <div className="container px-4 relative z-10">
        <div 
          ref={headerRef}
          className={`text-center max-w-3xl mx-auto mb-12 md:mb-20 transition-all duration-700 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-block px-4 py-2 mb-4 md:mb-6 glass-card rounded-full">
            <span className="text-sm font-semibold bg-gradient-to-r from-brand-pink via-brand-orange to-brand-cyan bg-clip-text text-transparent">
              Powerful Features
            </span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl md:text-6xl font-bold mb-4 md:mb-6">
            Simple tools.{" "}
            <span className="bg-gradient-to-r from-brand-pink via-brand-orange to-brand-cyan bg-clip-text text-transparent animate-gradient-shift">
              Powerful results.
            </span>
          </h2>
          
          <p className="text-lg md:text-xl text-muted-foreground">
            You don't need a full CRM. You need these five features — and nothing else.
          </p>
        </div>
        
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8 max-w-7xl mx-auto">
          {features.map((feature, index) => (
            <div 
              key={index}
              ref={(el) => (cardRefs.current[index] = el)}
              className={`group relative transition-all duration-700 ${
                visibleCards[index] 
                  ? 'opacity-100 translate-y-0' 
                  : 'opacity-0 translate-y-12'
              }`}
            >
              {/* Gradient Border Effect */}
              <div className={`absolute -inset-0.5 bg-gradient-to-r ${feature.gradient} rounded-2xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
              
              {/* Card Content */}
              <div className="relative glass-card-strong rounded-2xl p-5 md:p-8 h-full hover-lift border border-border/50 group-hover:border-border hover-3d-tilt touch-active">
                {/* Icon with Floating Animation */}
                <div className={`mb-4 md:mb-6 inline-flex p-3 md:p-4 rounded-xl ${feature.bgColor} ${feature.hoverBg} transition-smooth`}>
                  <span className="text-3xl md:text-4xl filter drop-shadow-lg">{feature.emoji}</span>
                </div>
                
                <h3 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 group-hover:text-primary transition-colors">
                  {feature.title}
                </h3>
                
                <div className="space-y-3 md:space-y-5">
                  {/* Pain Point */}
                  <div className="flex items-start gap-2 md:gap-3 p-3 md:p-4 rounded-lg bg-destructive/5 border border-destructive/10">
                    <X className="w-4 h-4 md:w-5 md:h-5 text-destructive flex-shrink-0 mt-0.5" />
                    <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                      {feature.pain}
                    </p>
                  </div>
                  
                  {/* Solution */}
                  <div className="flex items-start gap-2 md:gap-3 p-3 md:p-4 rounded-lg bg-primary/5 border border-primary/10">
                    <ArrowRight className={`w-4 h-4 md:w-5 md:h-5 ${feature.textColor} flex-shrink-0 mt-0.5 group-hover:translate-x-1 transition-transform`} />
                    <p className="text-xs md:text-sm text-foreground leading-relaxed font-medium">
                      {feature.solution}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
